import { useState } from "react";
import { Upload, Link2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Lets the teacher add a media item either by uploading a file or pasting a URL.
// Supports images, audio, video and PDFs (e.g. Canva exports).
export default function MediaUploader({ media, onChange }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("upload");
  const [type, setType] = useState("video");
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const addItem = (item) => onChange([...(media || []), item]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      addItem({ type: data.type, source: "upload", url: data.url, title: file.name });
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleEmbed = () => {
    if (!url.trim()) return;
    addItem({ type, source: "embed", url: url.trim(), title: "" });
    setUrl("");
  };

  const removeItem = (idx) => onChange(media.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button type="button" size="sm" variant={mode === "upload" ? "default" : "outline"}
          className="rounded-full" onClick={() => setMode("upload")} data-testid="media-mode-upload">
          <Upload className="h-4 w-4 mr-1" /> Upload
        </Button>
        <Button type="button" size="sm" variant={mode === "embed" ? "default" : "outline"}
          className="rounded-full" onClick={() => setMode("embed")} data-testid="media-mode-embed">
          <Link2 className="h-4 w-4 mr-1" /> URL
        </Button>
      </div>

      {mode === "upload" ? (
        <label className="flex items-center gap-2 text-sm cursor-pointer rounded-xl border border-dashed border-border px-4 py-3 hover:bg-muted/50">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span>{uploading ? t("common.loading") : "Choose file (image / audio / video / PDF)"}</span>
          <input type="file" className="hidden" accept="image/*,audio/*,video/*,application/pdf" onChange={handleUpload} data-testid="media-file-input" />
        </label>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="rounded-xl sm:w-32" data-testid="media-embed-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="video">Video</SelectItem>
              <SelectItem value="audio">Audio</SelectItem>
              <SelectItem value="image">Image</SelectItem>
              <SelectItem value="pdf">PDF</SelectItem>
            </SelectContent>
          </Select>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." className="rounded-xl" data-testid="media-embed-url" />
          <Button type="button" onClick={handleEmbed} className="rounded-full" data-testid="media-embed-add">{t("common.add")}</Button>
        </div>
      )}

      {(media || []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {media.map((m, i) => (
            <span key={i} className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs" data-testid={`media-chip-${i}`}>
              {m.type} · {m.source}
              <button type="button" onClick={() => removeItem(i)}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
